import type {
  QueueStatView,
  ApiClientView,
  AccountSessionView,
  AttachmentUploadView,
  AliasView,
  DnsRecordRequirement,
  DomainChallengeView,
  FolderView,
  LabelView,
  MailboxBatchView,
  MembershipView,
  DomainResource,
  Draft,
  DraftSeed,
  DeletionIntentCreationView,
  DeletionIntentInput,
  DeletionIntentView,
  ExportCreateInput,
  ExportCreationView,
  ExportDownloadGrantView,
  ExportView,
  FeatureCapability,
  Mailbox,
  Message,
  OperationView,
  ScheduledSendView,
  SessionView,
  SignatureView,
  Subscription,
  TenantEventView,
  Tenant,
  TotpEnrollmentView,
  ThreadSummary,
  VacationPolicyView,
  WebCapabilityId,
  WebhookDeliveryView,
  WebhookView,
} from "../domain/types";
import type { MailSearch } from "../domain/search";

export interface RequestContext {
  signal?: AbortSignal;
  requestId?: string;
}

export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly type: string,
    readonly detail: string,
    readonly requestId: string,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(detail);
    this.name = "ApiProblem";
  }
}

export interface BootstrapView {
  tenant: Tenant;
  domains: DomainResource[];
  mailboxes: Mailbox[];
  subscription: Subscription;
  apiClients: ApiClientView[];
  webhooks: WebhookView[];
  capabilities?: Partial<Record<WebCapabilityId, FeatureCapability>>;
}

export interface CursorPage<T> {
  data: T[];
  nextCursor?: string;
}

export interface EventPage {
  data: TenantEventView[];
  highWatermark: string;
  resnapshotRequired?: boolean;
}

export interface AttachmentUploadInput {
  draftId: string;
  file: File;
}

export interface ThreadPage {
  data: ThreadSummary[];
  nextCursor?: string;
  highWatermark: string;
  degradedReason?: string;
}

export interface ThreadView {
  thread: ThreadSummary;
  messages: Message[];
}

export interface SecretReveal<T> {
  resource: T;
  secret: string;
  recoverable: false;
}

export interface InboxFuryWebPort {
  bootstrap(context?: RequestContext): Promise<BootstrapView>;
  getSession(context?: RequestContext): Promise<SessionView>;
  signIn(input: { tenantId: string; email: string; password: string; totpCode?: string }, context?: RequestContext): Promise<SessionView>;
  signOut(context?: RequestContext): Promise<void>;
  reauthenticate(input: { password: string; totpCode: string }, context?: RequestContext): Promise<SessionView>;
  listAccountSessions(context?: RequestContext): Promise<AccountSessionView[]>;
  revokeAccountSession(sessionId: string, context?: RequestContext): Promise<void>;
  changePassword(input: { currentPassword: string; totpCode: string; newPassword: string }, context?: RequestContext): Promise<SessionView>;
  beginTotpEnrollment(context?: RequestContext): Promise<TotpEnrollmentView>;
  verifyTotpEnrollment(enrollmentId: string, totpCode: string, context?: RequestContext): Promise<{ session: SessionView; recoveryCodes: string[] }>;
  revokeAccount(confirmation: "REVOKE INBOXFURY ACCOUNT", context?: RequestContext): Promise<void>;
  listThreads(search: MailSearch, cursor?: string, context?: RequestContext): Promise<ThreadPage>;
  getThread(threadId: string, context?: RequestContext): Promise<ThreadView>;
  updateThreadState(
    threadId: string,
    update: { unread?: boolean; starred?: boolean; folder?: string },
    version: number,
    context?: RequestContext,
  ): Promise<ThreadSummary>;
  createDraft(draft: DraftSeed, idempotencyKey: string, context?: RequestContext): Promise<Draft>;
  saveDraft(draft: Draft, ifMatch: string, context?: RequestContext): Promise<Draft>;
  submitDraft(draftId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView>;
  listScheduledSends(mailboxId: string, cursor?: string, context?: RequestContext): Promise<CursorPage<ScheduledSendView>>;
  listOutboundSends(mailboxId: string, cursor?: string, context?: RequestContext): Promise<CursorPage<ScheduledSendView>>;
  cancelScheduledSend(sendIntentId: string, ifMatch: string, context?: RequestContext): Promise<OperationView>;
  uploadAttachment(input: AttachmentUploadInput, idempotencyKey: string, context?: RequestContext): Promise<AttachmentUploadView>;
  getAttachmentUpload(uploadId: string, context?: RequestContext): Promise<AttachmentUploadView>;
  createAttachmentDownloadGrant(attachmentId: string, idempotencyKey: string, context?: RequestContext): Promise<{ url: string; expiresAt: string }>;
  pollEvents(after: string, context?: RequestContext): Promise<EventPage>;
  getSignature(mailboxId: string, context?: RequestContext): Promise<SignatureView>;
  saveSignature(mailboxId: string, input: { senderIdentityId: string; plainText: string }, ifMatch: string, context?: RequestContext): Promise<SignatureView>;
  getVacationPolicy(mailboxId: string, context?: RequestContext): Promise<VacationPolicyView>;
  saveVacationPolicy(mailboxId: string, input: Omit<VacationPolicyView, "id" | "mailboxId" | "version" | "etag">, ifMatch: string, context?: RequestContext): Promise<VacationPolicyView>;
  requestDomain(name: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView>;
  /**
   * The records this domain's owner must publish, as issued by the server.
   *
   * Deliberately an operation rather than a client-side calculation. The
   * interface once fabricated these — hardcoded to another provider's SPF
   * include — and a customer following that screen exactly would have
   * authorised the wrong sender and had every message fail authentication.
   * The values a customer pastes into their registrar must come from the
   * system that will check them.
   */
  listDnsRequirements(domainId: string, context?: RequestContext): Promise<DnsRecordRequirement[]>;
  /**
   * What the server has actually observed at the customer's nameservers.
   *
   * Distinct from `listDnsRequirements`, which says what must be published.
   * "Still checking" and "seen, but wrong" are the two states a customer
   * refreshing the domain page is trying to tell apart.
   */
  listDomainChallenges(domainId: string, context?: RequestContext): Promise<DomainChallengeView[]>;
  requestMailbox(
    domainId: string,
    localPart: string,
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<OperationView>;
  /**
   * Bind a Mailcow domain this tenant does not yet own.
   *
   * Retrying domain create cannot do this: the adapter refuses the unowned
   * collision. Proof of control is a satisfied apex MX; DNS is not rewritten.
   */
  adoptDomain(domainId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView>;
  /**
   * Bind a Mailcow mailbox this tenant does not yet own.
   *
   * Retrying mailbox create cannot do this. The domain must already be adopted
   * and verified. An address that is not on the appliance cannot be adopted.
   */
  adoptMailbox(mailboxId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView>;
  getMailbox(mailboxId: string, context?: RequestContext): Promise<Mailbox>;
  /**
   * Change a mailbox that already exists.
   *
   * Returns an operation because the change is not real until the mail server
   * has it. Sending and receiving are separate fields: they fail independently,
   * and pausing a sender whose bounce rate has spiked almost never means you
   * want to stop reading its replies.
   */
  updateMailbox(
    mailboxId: string,
    changes: { displayName?: string; quotaBytes?: number; sendingEnabled?: boolean; receivingEnabled?: boolean },
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<OperationView>;
  /**
   * Issue new SMTP and IMAP passwords and revoke the old ones.
   *
   * Everything still authenticating with the old password stops working the
   * moment the new one verifies. That is the point of rotating, and it is also
   * how a rotation breaks an integration nobody remembered.
   */
  rotateMailboxCredentials(mailboxId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView>;
  /**
   * Create many mailboxes at once, across any number of domains.
   *
   * Not restricted to one domain: spreading senders over several is usually the
   * reason a tenant holds more than one, and a single-domain batch forces the
   * operator back into one request per domain, which is the thing this replaces.
   */
  createMailboxBatch(
    mailboxes: ReadonlyArray<{ domainId: string; localPart: string; quotaBytes?: number }>,
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<MailboxBatchView>;
  getMailboxBatch(batchId: string, context?: RequestContext): Promise<MailboxBatchView>;
  listFolders(mailboxId: string, context?: RequestContext): Promise<FolderView[]>;
  listLabels(mailboxId: string, context?: RequestContext): Promise<LabelView[]>;
  listAliases(context?: RequestContext): Promise<AliasView[]>;
  getAlias(aliasId: string, context?: RequestContext): Promise<AliasView>;
  createAlias(
    input: { address: string; destinationMailboxIds: string[] },
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<OperationView>;
  /**
   * Poll an asynchronous operation.
   *
   * Domain and mailbox creation both return an `OperationView` and then take
   * seconds to complete. Without this the interface starts the work, navigates
   * away and hopes.
   */
  /** Save the workspace name, postal address and timezone. */
  updateTenantProfile(
    input: { displayName?: string; physicalAddress?: string; timezone?: string },
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<Tenant>;

  /** Queue depth and recent throughput for this tenancy. */
  listQueueStats(context?: RequestContext): Promise<{ queues: QueueStatView[]; as_of: string }>;

  getOperation(operationId: string, context?: RequestContext): Promise<OperationView>;
  listMemberships(context?: RequestContext): Promise<MembershipView[]>;
  /** Revoke a membership. Invite and role-change have no API. */
  revokeMembership(membershipId: string, context?: RequestContext): Promise<void>;
  createApiClient(
    input: { name: string; scopes: string[]; mailboxRestrictions: string[] },
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<SecretReveal<ApiClientView>>;
  revokeApiClient(id: string, context?: RequestContext): Promise<ApiClientView>;
  createWebhook(
    input: { endpoint: string; eventPatterns: string[] },
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<SecretReveal<WebhookView>>;
  listWebhookDeliveries(webhookId: string, cursor?: string, context?: RequestContext): Promise<CursorPage<WebhookDeliveryView>>;
  replayWebhookDelivery(webhookId: string, deliveryId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView>;
  listExports(cursor?: string, context?: RequestContext): Promise<CursorPage<ExportView>>;
  createExport(input: ExportCreateInput, idempotencyKey: string, context?: RequestContext): Promise<ExportCreationView>;
  getExport(exportId: string, context?: RequestContext): Promise<ExportView>;
  createExportDownloadGrant(exportId: string, idempotencyKey: string, context?: RequestContext): Promise<ExportDownloadGrantView>;
  listDeletionIntents(cursor?: string, context?: RequestContext): Promise<CursorPage<DeletionIntentView>>;
  createDeletionIntent(input: DeletionIntentInput, idempotencyKey: string, context?: RequestContext): Promise<DeletionIntentCreationView>;
  getDeletionIntent(deletionIntentId: string, context?: RequestContext): Promise<DeletionIntentView>;
  confirmDeletionIntent(
    deletionIntentId: string,
    confirmationDigest: string,
    ifMatch: string,
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<DeletionIntentCreationView>;
  cancelDeletionIntent(
    deletionIntentId: string,
    ifMatch: string,
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<DeletionIntentView>;
  createBillingPortalSession(returnPath: string, idempotencyKey: string, context?: RequestContext): Promise<{ redirectUrl: string; expiresAt: string }>;
}

export function capability(capabilities: BootstrapView["capabilities"], id: WebCapabilityId): FeatureCapability {
  return capabilities?.[id] ?? {
    state: "disabled",
    reason: "This server route has not been qualified for this release.",
  };
}
